/**
 * How the storage AI-approval dialog reads a request (round B U29 / A20): a plain question for a heading ("Delete 3
 * storage items?") with the tool id as secondary detail, a severity that follows the operation (not always red), every
 * target listed ONCE, and any line saying access widens pulled to the top.
 *
 * Since the A+B merge main sends what the renderer used to work out itself (round B follow-up B4 / B5): the severity is
 * `request.consequence` — the request's strongest operation, classified in `electron/mcp-storage/
 * storageApprovalConsequence.ts` for every approval-asking tool — and the summary never repeats a target (the target
 * list carries each one; `storageApprovalTargets.registry.test.ts` pins both). The per-tool severity below is only a
 * fallback for a request without a consequence.
 *
 * The main process sends `title: "Approve <tool>"`; the tool id is read from it until the request carries it.
 */
import type { McpStorageWriteApprovalRequest } from '../../../types/mcpUiControl';

export type StorageApprovalSeverity = 'destructive' | 'modifies' | 'creates';

type ToolCopy = { heading: (count?: number, targets?: readonly string[]) => string; severity: StorageApprovalSeverity };

const plural = (count: number | undefined, one: string, many: string) =>
  count === undefined ? many : `${count} ${count === 1 ? one : many}`;

/**
 * Final UI/UX batch C1 (R14 / N15, review C1-03): an entity batch is headed by what it does — each operation with its
 * count, deletes first ("Delete 1 table entity and merge 1 table entity?"), never a "Write …" that hides a delete. The
 * counts come from main's target lines, which start with the operation (`entityWriteApprovalTargets`:
 * `Delete PartitionKey "…", RowKey "…"`; the keys follow JSON-quoted, so no key can fake the prefix) — the coupling test
 * runs this over the real builder. Lines that do not all parse fall back to "Write N table entities?".
 */
const ENTITY_OPERATIONS = [['Delete', 'delete'], ['Replace', 'replace'], ['Merge', 'merge'], ['Create', 'create']] as const;
const joinWithAnd = (parts: string[]): string => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

function entityBatchHeading(count: number | undefined, targets: readonly string[] | undefined): string {
  const fallback = `Write ${plural(count, 'table entity', 'table entities')}?`;
  if (!targets || targets.length === 0) return fallback;
  const counts = ENTITY_OPERATIONS
    .map(([label, verb]) => ({ verb, n: targets.filter((line) => line.startsWith(`${label} PartitionKey `)).length }))
    .filter(({ n }) => n > 0);
  if (counts.reduce((sum, { n }) => sum + n, 0) !== targets.length) return fallback;
  const parts = counts.map(({ verb, n }, index) => `${index === 0 ? verb[0].toUpperCase() + verb.slice(1) : verb} ${plural(n, 'table entity', 'table entities')}`);
  return `${joinWithAnd(parts)}?`;
}

const TOOL_COPY: Record<string, ToolCopy> = {
  storage_delete: { heading: (n) => `Delete ${plural(n, 'storage item', 'storage items')}?`, severity: 'destructive' },
  storage_delete_table: { heading: () => 'Delete a table?', severity: 'destructive' },
  storage_clear_queue: { heading: () => 'Clear every message in a queue?', severity: 'destructive' },
  storage_receive_messages: { heading: () => 'Receive (dequeue) queue messages?', severity: 'modifies' },
  storage_upload: { heading: (n) => `Upload ${plural(n, 'item', 'items')}?`, severity: 'modifies' },
  storage_set_acl: { heading: () => 'Change access control?', severity: 'modifies' },
  storage_move_path: { heading: () => 'Move or rename a path?', severity: 'modifies' },
  storage_write_entities: { heading: entityBatchHeading, severity: 'modifies' },
  storage_set_share_quota: { heading: () => 'Change a file share quota?', severity: 'modifies' },
  storage_share_snapshots: { heading: () => 'Change file share snapshots?', severity: 'modifies' },
  storage_generate_sas: { heading: () => 'Generate a shared access signature?', severity: 'modifies' },
  storage_create_container: { heading: () => 'Create a container?', severity: 'creates' },
  storage_create_directory: { heading: () => 'Create a folder?', severity: 'creates' },
  storage_create_share: { heading: () => 'Create a file share?', severity: 'creates' },
  storage_create_table: { heading: () => 'Create a table?', severity: 'creates' },
  storage_send_message: { heading: () => 'Send a queue message?', severity: 'creates' },
};

export const SEVERITY_LABEL: Record<StorageApprovalSeverity, string> = {
  destructive: 'Removes data',
  modifies: 'Changes data',
  creates: 'Adds data',
};

const FROM_CONSEQUENCE: Record<NonNullable<McpStorageWriteApprovalRequest['consequence']>, StorageApprovalSeverity> = {
  removes: 'destructive',
  changes: 'modifies',
  adds: 'creates',
};

const WIDENS = /widen/iu;

export type StorageApprovalPresentation = {
  tool?: string;
  heading: string;
  severity: StorageApprovalSeverity;
  /** Lines saying access widens — shown first, highlighted. */
  risks: string[];
  /** The consequence summary (never a target: those are the request's target list). */
  summary: string[];
};

export function storageApprovalPresentation(request: McpStorageWriteApprovalRequest): StorageApprovalPresentation {
  const tool = /^Approve (storage_[a-z_]+)$/u.exec(request.title)?.[1];
  const copy = tool ? TOOL_COPY[tool] : undefined;
  const count = request.targets?.lines.length;
  const severity = (request.consequence ? FROM_CONSEQUENCE[request.consequence] : undefined) ?? copy?.severity ?? 'modifies';
  return {
    tool,
    heading: copy ? copy.heading(count, request.targets?.lines) : request.title,
    severity,
    risks: request.summaryLines.filter((line) => WIDENS.test(line)),
    summary: request.summaryLines.filter((line) => !WIDENS.test(line)),
  };
}
